import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["odometer", "kpis", "zones", "timestamp"]
  static values = {
    slug: String,
    interval: { type: Number, default: 60000 }  // Default 1 minute fallback
  }

  connect() {
    this.currentValue = this.getOdometerValue()
    this.initializeOdometer()
    this.subscribeToChannel()
    this.startAutoRefresh()  // Fallback polling

    // Handle resize for responsive odometer
    this.boundHandleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.boundHandleResize)
  }

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: "DashboardChannel", slug: this.slugValue },
      {
        received: (data) => {
          console.log("Dashboard update received:", data)
          this.updateDashboard(data)
        },
        connected: () => {
          console.log("Connected to DashboardChannel")
        },
        disconnected: () => {
          console.log("Disconnected from DashboardChannel")
        }
      }
    )
  }

  handleResize() {
    // Debounce resize handling
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.initializeOdometer()
    }, 100)
  }

  initializeOdometer() {
    if (!this.hasOdometerTarget) return

    const digitHeight = this.getDigitHeight()
    const wheels = this.odometerTarget.querySelectorAll('.odometer-wheel')

    wheels.forEach(wheel => {
      const digit = parseInt(wheel.dataset.digit) || 0
      wheel.style.transform = `translateY(-${digit * digitHeight}px)`
    })
  }

  disconnect() {
    this.stopAutoRefresh()
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    window.removeEventListener('resize', this.boundHandleResize)
    clearTimeout(this.resizeTimeout)
  }

  startAutoRefresh() {
    this.refreshTimer = setInterval(() => {
      this.fetchData()
    }, this.intervalValue)
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  }

  getOdometerValue() {
    if (!this.hasOdometerTarget) return 0
    return parseInt(this.odometerTarget.dataset.value) || 0
  }

  async fetchData() {
    try {
      const response = await fetch(`/e/${this.slugValue}/data`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) return

      const data = await response.json()
      this.updateDashboard(data)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    }
  }

  updateDashboard(data) {
    // Animate odometer
    if (data.total_liters !== undefined) {
      this.animateOdometer(Math.floor(data.total_liters))
    }

    // Update KPIs
    if (data.kpis && this.hasKpisTarget) {
      this.updateKpis(data.kpis, data.total_liters)
    }

    // Update zones
    if (data.zones && this.hasZonesTarget) {
      this.updateZones(data.zones)
    }

    // Update timestamp
    if (this.hasTimestampTarget && data.updated_at) {
      const date = new Date(data.updated_at)
      this.timestampTarget.textContent = date.toLocaleTimeString()
    }
  }

  getDigitHeight() {
    // Get the actual height of a digit element (responsive)
    const digit = this.odometerTarget?.querySelector('.odometer-digit')
    return digit ? digit.offsetHeight : 100
  }

  animateOdometer(targetValue) {
    if (!this.hasOdometerTarget) return

    const startValue = this.currentValue
    if (targetValue === startValue) return

    // Format the new value with thousand separators
    const formattedTarget = this.formatNumberWithDots(targetValue)
    const formattedStart = this.formatNumberWithDots(startValue)

    // Rebuild odometer if digit count changed
    if (formattedTarget.length !== formattedStart.length) {
      this.rebuildOdometer(targetValue)
      this.currentValue = targetValue
      this.odometerTarget.dataset.value = targetValue
      return
    }

    // Get current digit height (responsive)
    const digitHeight = this.getDigitHeight()

    // Animate each digit with staggered delays
    const digits = this.odometerTarget.querySelectorAll('.odometer-digit')
    const targetChars = formattedTarget.split('')
    const startChars = formattedStart.split('')

    let digitIndex = 0
    targetChars.forEach((char, index) => {
      if (char === '.') return // Skip separators

      const digit = digits[digitIndex]
      if (!digit) {
        digitIndex++
        return
      }

      const wheel = digit.querySelector('.odometer-wheel')
      const currentDigit = parseInt(startChars[index]) || 0
      const targetDigit = parseInt(char) || 0

      if (currentDigit !== targetDigit) {
        // Add random delay for natural effect (0-500ms)
        const delay = Math.random() * 500

        setTimeout(() => {
          wheel.style.transform = `translateY(-${targetDigit * digitHeight}px)`
          wheel.dataset.digit = targetDigit
        }, delay)
      }

      digitIndex++
    })

    this.currentValue = targetValue
    this.odometerTarget.dataset.value = targetValue
  }

  rebuildOdometer(value) {
    const formatted = this.formatNumberWithDots(value)
    const digitHeight = this.getDigitHeight()
    let html = ''

    formatted.split('').forEach((char, index) => {
      if (char === '.') {
        html += '<div class="odometer-separator">.</div>'
      } else {
        const digit = parseInt(char) || 0
        html += `
          <div class="odometer-digit" data-digit-index="${index}">
            <div class="odometer-wheel" data-digit="${digit}" style="transform: translateY(-${digit * digitHeight}px);">
              ${[0,1,2,3,4,5,6,7,8,9].map(n => `<div class="odometer-number">${n}</div>`).join('')}
            </div>
          </div>
        `
      }
    })

    this.odometerTarget.innerHTML = html
  }

  updateKpis(kpis, totalLiters) {
    // Update liters KPI
    const litersEl = this.kpisTarget.querySelector('[data-kpi="liters"]')
    if (litersEl && totalLiters !== undefined) {
      this.animateKpiValue(litersEl, Math.round(totalLiters))
    }

    // Update carbon KPI
    const carbonEl = this.kpisTarget.querySelector('[data-kpi="carbon"]')
    if (carbonEl && kpis.carbon_saved !== undefined) {
      this.animateKpiValue(carbonEl, Math.round(kpis.carbon_saved))
    }

    // Update plastic KPI
    const plasticEl = this.kpisTarget.querySelector('[data-kpi="plastic"]')
    if (plasticEl && kpis.plastic_saved !== undefined) {
      this.animateKpiValue(plasticEl, Math.round(kpis.plastic_saved))
    }

    // Update water footprint KPI
    const waterEl = this.kpisTarget.querySelector('[data-kpi="water"]')
    if (waterEl && kpis.water_footprint_saved !== undefined) {
      this.animateKpiValue(waterEl, Math.round(kpis.water_footprint_saved))
    }
  }

  animateKpiValue(element, targetValue) {
    const currentValue = this.parseFormattedNumber(element.textContent)
    if (currentValue === targetValue) return

    const duration = 1500 // 1.5 seconds
    const steps = 30
    const stepDuration = duration / steps
    const stepValue = (targetValue - currentValue) / steps

    let step = 0
    const animate = () => {
      step++
      const currentStep = currentValue + (stepValue * step)

      if (step < steps) {
        element.textContent = this.formatNumberWithDots(Math.round(currentStep))
        setTimeout(animate, stepDuration)
      } else {
        element.textContent = this.formatNumberWithDots(targetValue)
      }
    }

    animate()
  }

  updateZones(zones) {
    // Find max value for percentage calculation
    const maxLiters = Math.max(...zones.map(z => z.liters), 1)

    zones.forEach(zone => {
      // Update value text
      const valueEl = this.zonesTarget.querySelector(`[data-zone-liters="${zone.id}"]`)
      if (valueEl) {
        this.animateKpiValue(valueEl, Math.round(zone.liters))
      }

      // Update bar height
      const barEl = this.zonesTarget.querySelector(`[data-zone-bar="${zone.id}"]`)
      if (barEl) {
        const percentage = (zone.liters / maxLiters * 100).toFixed(1)
        barEl.style.height = `${percentage}%`
      }
    })
  }

  parseFormattedNumber(str) {
    // Parse number formatted with dots as thousand separators
    return parseInt(str.replace(/\./g, '')) || 0
  }

  formatNumberWithDots(num) {
    // Format number with dots as thousand separators (Spanish/European format)
    return num.toLocaleString('es-ES', { maximumFractionDigits: 0 })
  }
}
